import { useEffect, useState } from "react";
import {
  CheckCircle2Icon,
  CircleIcon,
  CopyIcon,
  DownloadIcon,
  GiftIcon,
  Loader2Icon,
  LogOutIcon,
  PenLineIcon,
  UploadIcon,
  WalletIcon,
  XIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Button, Badge } from "../ui/button";
import { Table, TBody, TD, TFoot, TH, THead, TR } from "../ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/overlay";
import { MobileItem, MobileList, Desktop } from "../ui/card";
import { cn } from "../lib/cn";
import { round2, short, shortTx, sum, wpOf } from "../lib/format";
import { useN, useT, useTT, Y } from "../i18n";
import { ServiceIcon } from "../lib/icons";
import { useStore, type AuthRow } from "../store/store";
import { useWallet } from "../store/wallet";
import { RateLine, StatusDot } from "./common";
import { useTotalWp } from "./WalletBlock";
import { navigate } from "./Shell";
import { useStopOnChain } from "./signing";

const ethText = (n: number) => n.toFixed(6);

function LineIcon({ state }: { state: string }) {
  return state === "signed" ? (
    <CheckCircle2Icon className="mt-0.5 size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
  ) : state === "signing" ? (
    <Loader2Icon className="mt-0.5 size-3.5 shrink-0 animate-spin" />
  ) : state === "failed" ? (
    <XIcon className="mt-0.5 size-3.5 shrink-0 text-destructive" />
  ) : (
    <CircleIcon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
  );
}

function TokenAmount({ amount, symbol }: { amount: number; symbol: string }) {
  const { directory } = useStore();
  const n = useN();
  const entry = directory.find((e) => e.token === symbol);
  return (
    <span className="inline-flex items-center gap-1" title={entry?.name}>
      <ServiceIcon entry={entry} kind="tool" className="size-3.5" />
      <span className="tabular-nums">{n(amount)}</span>
      <span className="font-mono">{symbol}</span>
    </span>
  );
}

function CopyAddress({ address }: { address: string }) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(address);
          toast.success(t("wallet.copied"));
        } catch {
          toast.error(t("wallet.copyBlocked"));
        }
      }}
      aria-label={t("wallet.copyAddress")}
      className="inline-flex items-center gap-0.5 rounded hover:text-foreground"
    >
      {short(address)}
      <CopyIcon className="size-3" />
    </button>
  );
}

/** The signing panel: the request's purpose, the amount, the lines in plain words, Sign and Reject (D32, D45). */
function SigningPanel() {
  const { queue, lines, phase, sign, reject } = useWallet();
  const { wallet, config } = useStore();
  const t = useT();
  const tt = useTT();
  const n = useN();
  const [openId, setOpenId] = useState<string | null>(null);
  const req = queue[0]!;
  const gasTotal = req.lines.reduce((a, l) => a + (l.gas ?? 0), 0);
  const messagesOnly = req.lines.every((l) => l.gas === undefined);
  const expanded = openId === req.id || phase !== "ask";
  const count = req.lines.length;
  const queued = queue.length - 1;
  return (
    <>
      <div className="flex items-center gap-2 border-b px-4 py-3 text-xs text-muted-foreground">
        <WalletIcon className="size-4 text-foreground" />
        <span className="font-medium tracking-wide uppercase">{t("wallet.request")}</span>
        <span className="ml-auto font-mono">{wallet ? short(wallet.address) : ""}</span>
      </div>
      <div className="flex min-h-0 flex-col gap-3 overflow-y-auto p-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-base leading-snug font-semibold">{tt(req.title)}</h2>
          {req.amount !== undefined && (
            <div className="text-[28px] leading-9 font-semibold tabular-nums">
              ≈ {n(round2(req.amount))} <span className="text-base font-medium text-muted-foreground">wp</span>
            </div>
          )}
          {(req.tokens || req.note) && (
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-muted-foreground">
              {req.tokens?.map((tk) => (
                <TokenAmount key={tk.symbol} amount={tk.amount} symbol={tk.symbol} />
              ))}
              {req.note && <span>{tt(req.note)}</span>}
            </div>
          )}
          <p className="text-xs text-muted-foreground">{t("wallet.from", { network: config.networkName })}</p>
        </div>
        <div className="rounded-lg border">
          <button
            type="button"
            aria-expanded={expanded}
            disabled={phase !== "ask"}
            onClick={() => setOpenId(expanded ? null : req.id)}
            className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm"
          >
            {messagesOnly
              ? count === 1
                ? t("wallet.detailsMessage")
                : t("wallet.detailsMessageN", { n: count })
              : count === 1
                ? t("wallet.details1")
                : t("wallet.detailsN", { n: count })}
            <span className={cn("size-4 text-muted-foreground transition-transform", expanded && "rotate-180")}>⌄</span>
          </button>
          {expanded && (
            <ol className="flex flex-col divide-y border-t">
              {req.lines.map((line, i) => {
                const s = lines[i] ?? { state: "pending" };
                const code = tt(line.text).replace(/^\d+\.\s+/, "");
                return (
                  <li key={i} className="flex items-start gap-2 px-3 py-2">
                    <LineIcon state={s.state} />
                    <div className="min-w-0 flex-1">
                      <div className={cn("text-sm", !line.plain && "font-mono text-xs break-words")}>
                        {line.plain ? tt(line.plain) : code}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 font-mono text-[11px] text-muted-foreground">
                        {line.plain && <span className="break-all">{code}</span>}
                        {line.address && (
                          <>
                            {line.plain && <span aria-hidden>·</span>}
                            <CopyAddress address={line.address} />
                          </>
                        )}
                        <span aria-hidden>·</span>
                        <span>
                          {line.gas === undefined ? t("wallet.noGas") : t("wallet.gas", { eth: ethText(line.gas) })}
                        </span>
                      </div>
                      {line.calls && (
                        <ul className="mt-1 flex flex-col gap-0.5 border-l pl-2 font-mono text-[11px] text-muted-foreground">
                          {line.calls.map((c, j) => (
                            <li key={j} className="break-all">
                              {c}
                            </li>
                          ))}
                        </ul>
                      )}
                      {s.state === "signed" && (
                        <div className="mt-0.5 text-xs text-emerald-600 dark:text-emerald-400">
                          {s.tx ? t("wallet.signedTx", { tx: shortTx(s.tx) }) : t("common.signed")}
                        </div>
                      )}
                      {s.state === "failed" && <div className="mt-0.5 text-xs text-destructive">{s.error}</div>}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
        {!messagesOnly && (
          <p className="text-xs text-muted-foreground">{t("wallet.fee", { eth: ethText(gasTotal) })}</p>
        )}
        {queued > 0 && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            {queued === 1 ? t("wallet.queued1") : t("wallet.queuedN", { n: queued })}
          </p>
        )}
      </div>
      <div className="flex gap-2 border-t p-3">
        <Button variant="outline" className="flex-1" onClick={reject} disabled={phase !== "ask"}>
          <XIcon />
          {t("wallet.reject")}
        </Button>
        <Button className="flex-1" onClick={() => void sign()} disabled={phase !== "ask"}>
          {phase === "signing" ? <Loader2Icon className="animate-spin" /> : <PenLineIcon />}
          {t(phase === "signing" ? "wallet.signing" : "common.sign")}
        </Button>
      </div>
    </>
  );
}

function Balances() {
  const { balances, wpPerToken, exchanges, tokens, config, directory } = useStore();
  const t = useT();
  const n = useN();
  const tt = useTT();
  const rows = Object.entries(balances).sort(([a], [b]) =>
    a === "wpCredit" ? -1 : b === "wpCredit" ? 1 : a.localeCompare(b),
  );
  const ownerOf = (token: string) => {
    const meta = tokens.get(token);
    const e = directory.find((x) => x.tokenAddress.toLowerCase() === meta?.address.toLowerCase());
    return e ? e.name.split("/")[0]! : short(meta?.owner ?? "");
  };
  return (
    <section className="flex flex-col gap-2">
      <p className="text-xs text-muted-foreground">{t("wallet.balances.description")}</p>
      <p className="inline-flex items-center gap-1.5 text-xs">
        <GiftIcon className="size-3.5 text-muted-foreground" />
        {t("wallet.starter", { provider: config.provider, tokens: "50 tRunner · 50 tLLM · 50 tMemory · 20 tCloud" })}
      </p>
      <Table>
        <THead>
          <TR>
            <TH>{t("col.token")}</TH>
            <TH>{t("col.kind")}</TH>
            <TH className="text-right">{t("col.balance")}</TH>
            <TH className="text-right">{t("col.wp")}</TH>
          </TR>
        </THead>
        <TBody>
          {rows.map(([sym, bal]) => (
            <TR key={sym}>
              <TD>
                <div className="font-mono">{sym}</div>
                <RateLine token={sym} buy className="text-[11px]" />
              </TD>
              <TD>
                {sym === "wpCredit" ? (
                  <Badge variant="secondary" className="h-4 px-1.5 text-[10px] font-normal">
                    {t("tokenKind.credit")}
                  </Badge>
                ) : (
                  <span className="text-xs text-muted-foreground">{t("tokenKind.direct")}</span>
                )}
              </TD>
              <TD className="text-right font-mono tabular-nums">{n(bal)}</TD>
              <TD className="text-right text-muted-foreground tabular-nums">{n(round2(bal * wpPerToken(sym)))}</TD>
            </TR>
          ))}
        </TBody>
        <TFoot>
          <TR>
            <TD colSpan={4} className="text-right font-medium">
              {t("common.total", { n: sum(...rows.map(([s, b]) => b * wpPerToken(s))) })}
            </TD>
          </TR>
        </TFoot>
      </Table>
      <h3 className="mt-3 text-sm font-medium">{t("wallet.exchanges.title")}</h3>
      <p className="text-xs text-muted-foreground">{t("wallet.exchanges.description")}</p>
      {exchanges.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("wallet.exchanges.empty")}</p>
      ) : (
        <ul className="flex flex-col gap-1 text-xs">
          {exchanges.map((e, i) => {
            const meta = [...tokens.values()].find((m) => m.address.toLowerCase() === e.token.toLowerCase());
            return (
              <li key={i} className="flex flex-wrap justify-between gap-x-3 border-b py-1 last:border-b-0">
                <span className="font-mono">
                  {t("wallet.exchanges.row", {
                    credits: wpOf(e.credits),
                    tokens: Number(e.tokens),
                    token: meta?.symbol ?? short(e.token),
                    owner: ownerOf(meta?.symbol ?? ""),
                  })}
                </span>
                <span className="text-muted-foreground">{tt(shortTx(e.tx))}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

const openRows = (rows: AuthRow[]) => rows.filter((r) => r.remaining > 0);

function Authorizations({ onLeave }: { onLeave: () => void }) {
  const { conversations, auths, wpPerToken } = useStore();
  const t = useT();
  const n = useN();
  const stopOnChain = useStopOnChain();
  const groups = conversations.map((c) => {
    const rows = auths[c.id] ?? [];
    return {
      c,
      rows,
      capWp: sum(...rows.map((r) => r.cap * wpPerToken(r.token))),
      remainingWp: sum(...rows.map((r) => r.remaining * wpPerToken(r.token))),
    };
  });
  const withOpen = groups.filter((g) => openRows(g.rows).length > 0);
  const head = (g: (typeof groups)[number]) => (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <a className="font-medium hover:underline" href={`#/conversations/${g.c.id}`} onClick={onLeave}>
        {g.c.title || t("conv.untitled")}
      </a>
      <span className="font-mono text-xs text-muted-foreground">{short(g.c.agent)}</span>
      <StatusDot status={g.c.status} />
      <span className="text-xs text-muted-foreground">
        {t("wallet.auth.totals", { cap: g.capWp, remaining: g.remainingWp })}
      </span>
    </div>
  );
  const stopButton = (g: (typeof groups)[number]) => (
    <Button
      variant="destructive"
      size="xs"
      disabled={openRows(g.rows).length === 0}
      onClick={() => stopOnChain(g.c, openRows(g.rows))}
    >
      {t("conv.stop.onChain")}
    </Button>
  );
  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <p className="text-xs text-muted-foreground">{t("wallet.auth.description")}</p>
        <Button
          variant="destructive"
          size="xs"
          className="shrink-0"
          disabled={withOpen.length === 0}
          onClick={() => {
            for (const g of withOpen) stopOnChain(g.c, openRows(g.rows));
          }}
        >
          {t("common.stopAll")}
        </Button>
      </div>
      <MobileList>
        {groups.map((g) => (
          <MobileItem key={g.c.id} className="gap-2">
            <div className="flex items-start justify-between gap-3">
              {head(g)}
              {stopButton(g)}
            </div>
            {g.rows.map((r) => (
              <div key={r.token} className="flex flex-col gap-1 rounded-lg bg-muted/50 p-2 text-xs">
                <span className="font-mono">{r.token}</span>
                <div className="grid grid-cols-4 gap-2">
                  {(["cap", "used", "remaining"] as const).map((f) => (
                    <div key={f}>
                      <div className="text-muted-foreground">{t(`col.${f}`)}</div>
                      <div className="tabular-nums">{n(r[f])}</div>
                    </div>
                  ))}
                  <div>
                    <div className="text-muted-foreground">{t("col.wp")}</div>
                    <div className="tabular-nums">{n(round2(r.remaining * wpPerToken(r.token)))}</div>
                  </div>
                </div>
              </div>
            ))}
          </MobileItem>
        ))}
      </MobileList>
      <Desktop>
        <Table>
          <THead>
            <TR>
              <TH>{t("col.token")}</TH>
              <TH className="text-right">{t("col.cap")}</TH>
              <TH className="text-right">{t("col.used")}</TH>
              <TH className="text-right">{t("col.remaining")}</TH>
              <TH className="text-right">{t("col.wp")}</TH>
              <TH className="text-right" />
            </TR>
          </THead>
          {groups.map((g) => (
            <TBody key={g.c.id} className="border-b last:border-b-0">
              <TR className="bg-muted/50 hover:bg-muted/50">
                <TD colSpan={5} className="whitespace-normal">
                  {head(g)}
                </TD>
                <TD className="text-right">{stopButton(g)}</TD>
              </TR>
              {g.rows.map((r) => (
                <TR key={r.token}>
                  <TD className="pl-6 font-mono">{r.token}</TD>
                  <TD className="text-right tabular-nums">{n(r.cap)}</TD>
                  <TD className="text-right tabular-nums">{n(r.used)}</TD>
                  <TD className="text-right tabular-nums">{n(r.remaining)}</TD>
                  <TD className="text-right text-muted-foreground tabular-nums">
                    {n(round2(r.remaining * wpPerToken(r.token)))}
                  </TD>
                  <TD />
                </TR>
              ))}
            </TBody>
          ))}
        </Table>
      </Desktop>
    </section>
  );
}

function KeyDialogs({ exportOpen, setExportOpen }: { exportOpen: boolean; setExportOpen: (v: boolean) => void }) {
  const { wallet } = useStore();
  const t = useT();
  return (
    <Dialog open={exportOpen} onOpenChange={setExportOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("wallet.export.title")}</DialogTitle>
          <DialogDescription>{t("wallet.export.description")}</DialogDescription>
        </DialogHeader>
        <code className="rounded-lg border bg-muted/40 p-2 font-mono text-xs break-all">{wallet?.key}</code>
        <DialogFooter>
          <Button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(wallet?.key ?? "");
                toast.success(t("wallet.copiedKey"));
              } catch {
                toast.error(t("wallet.copyBlocked"));
              }
            }}
          >
            <CopyIcon />
            {t("wallet.export.copy")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WalletPanel() {
  const { closePanel, tab, setTab } = useWallet();
  const { wallet, config, ethBalance, signOut, forget } = useStore();
  const t = useT();
  const n = useN();
  const total = useTotalWp();
  const [exportOpen, setExportOpen] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(wallet?.address ?? "");
      toast.success(t("wallet.copied"));
    } catch {
      toast.error(t("wallet.copyBlocked"));
    }
  };
  const doSignOut = () => {
    closePanel();
    signOut();
    navigate(`/${window.location.hash.replace(/^#\/?/, "").split("/")[0] || "conversations"}`);
  };
  return (
    <Tabs
      value={tab}
      onValueChange={(v) => setTab(v as "balances" | "authorizations")}
      className="flex min-h-0 flex-1 flex-col gap-0"
    >
      <div className="flex items-start gap-3 px-4 pt-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">{t("wallet.card.title")}</h2>
          <p className="text-xs text-muted-foreground">{t("wallet.card.description")}</p>
        </div>
        <Button variant="ghost" size="icon-sm" onClick={closePanel} aria-label={t("wallet.panel.close")}>
          <XIcon />
        </Button>
      </div>
      <div className="flex flex-col gap-3 border-b px-4 pt-3 pb-3">
        <div className="grid grid-cols-3 gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto_auto_auto] sm:gap-x-6">
          <div className="col-span-3 min-w-0 sm:col-span-1">
            <div className="text-xs text-muted-foreground">{t("wallet.address")}</div>
            <div className="mt-1 flex items-center gap-1">
              <code className="min-w-0 truncate font-mono text-sm" title={wallet?.address}>
                {wallet?.address}
              </code>
              <Button variant="ghost" size="icon-sm" onClick={copy} aria-label={t("wallet.copyAddress")}>
                <CopyIcon />
              </Button>
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">{t("wallet.network")}</div>
            <div className="mt-1 text-sm font-medium">{config.networkName}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">{t("wallet.eth")}</div>
            <div className="mt-1 font-mono text-sm font-medium">{(Number(ethBalance) / 1e18).toFixed(6)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">{t("wallet.total")}</div>
            <div className="mt-1 text-sm font-medium tabular-nums">{t("common.wp", { n: total })}</div>
          </div>
        </div>
        <TabsList className="w-full sm:w-fit">
          <TabsTrigger value="balances">{t("wallet.tab.balances")}</TabsTrigger>
          <TabsTrigger value="authorizations">{t("wallet.auth.title")}</TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="balances" className="min-h-0 flex-1 overflow-y-auto p-4">
        <Balances />
      </TabsContent>
      <TabsContent value="authorizations" className="min-h-0 flex-1 overflow-y-auto p-4">
        <Authorizations onLeave={closePanel} />
      </TabsContent>
      <div className="flex flex-wrap gap-2 border-t p-3">
        <Button variant="outline" size="sm" onClick={() => setExportOpen(true)}>
          <DownloadIcon />
          {t("wallet.exportKey")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (window.confirm(t("wallet.forgetConfirm", { address: short(wallet?.address ?? "") }))) {
              closePanel();
              forget();
            }
          }}
        >
          <UploadIcon />
          {t("wallet.forget")}
        </Button>
        <Button variant="outline" size="sm" className="ml-auto" onClick={doSignOut}>
          <LogOutIcon />
          {t("shell.signOut")}
        </Button>
      </div>
      <KeyDialogs exportOpen={exportOpen} setExportOpen={setExportOpen} />
      <span className="sr-only">{n(0)}</span>
    </Tabs>
  );
}

function WalletPopover() {
  const { conversations, auths, balances, wpPerToken, wallet, config, ethBalance, signOut } = useStore();
  const { openPanel, closePopover } = useWallet();
  const t = useT();
  const n = useN();
  const total = useTotalWp();
  const active = conversations.reduce((a, c) => a + (auths[c.id] ?? []).filter((r) => r.remaining > 0).length, 0);
  const largest = Object.entries(balances)
    .map(([sym, b]) => ({ sym, b, wp: b * wpPerToken(sym) }))
    .sort((a, b) => b.wp - a.wp)
    .slice(0, 4);
  return (
    <div className="flex flex-col gap-4 overflow-y-auto p-4">
      <div className="flex items-center gap-1.5">
        <WalletIcon className="size-4 shrink-0" />
        <code className="font-mono text-sm" title={wallet?.address}>
          {short(wallet?.address ?? "")}
        </code>
        <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="size-1.5 rounded-full bg-emerald-500" />
          {config.networkName}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-xs text-muted-foreground">{t("wallet.total")}</div>
          <div className="text-lg leading-7 font-semibold tabular-nums">{t("common.wp", { n: total })}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">{t("wallet.eth")}</div>
          <div className="font-mono text-sm leading-7">{(Number(ethBalance) / 1e18).toFixed(6)}</div>
        </div>
      </div>
      <p className="text-sm">
        {active === 1 ? t("wallet.popover.active1") : t("wallet.popover.activeN", { n: active })}
      </p>
      <div>
        <div className="mb-1.5 text-xs text-muted-foreground">{t("wallet.popover.largest")}</div>
        <ul className="divide-y rounded-lg border text-sm">
          {largest.map((e) => (
            <li key={e.sym} className="flex items-baseline justify-between gap-3 px-3 py-1.5">
              <span className="font-mono">{e.sym}</span>
              <span className="tabular-nums">
                {n(e.b)} <span className="text-xs text-muted-foreground">{t("common.wp", { n: round2(e.wp) })}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div className="flex gap-2">
        <Button className="flex-1" onClick={openPanel}>
          <WalletIcon />
          {t("wallet.open")}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            closePopover();
            signOut();
          }}
        >
          <LogOutIcon />
          {t("shell.signOut")}
        </Button>
      </div>
    </div>
  );
}

const FRAME: Record<string, string> = {
  signing:
    "inset-x-0 bottom-0 max-h-[85svh] rounded-t-2xl md:inset-x-auto md:bottom-4 md:left-[15.5rem] md:max-h-[calc(100svh-2rem)] md:w-[26rem] md:rounded-2xl",
  wallet:
    "inset-0 md:inset-auto md:bottom-4 md:left-[15.5rem] md:max-h-[calc(100svh-2rem)] md:w-[min(50rem,calc(100vw-17rem))] md:rounded-2xl md:border",
  popover:
    "inset-x-0 bottom-0 max-h-[85svh] rounded-t-2xl md:inset-x-auto md:bottom-[5.25rem] md:left-3 md:w-80 md:rounded-xl",
};

/** The wallet slides out of its sidebar block: the signing panel, the popover or the full panel (D32). */
export function WalletOverlay() {
  const { queue, popoverOpen, closePopover, panelOpen, closePanel } = useWallet();
  const { signedIn } = useStore();
  const t = useT();
  const kind = queue.length > 0 ? "signing" : signedIn ? (panelOpen ? "wallet" : popoverOpen ? "popover" : null) : null;
  const close = kind === "popover" ? closePopover : kind === "wallet" ? closePanel : undefined;
  useEffect(() => {
    if (!close) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);
  if (!kind) return null;
  return (
    <>
      <div
        aria-hidden
        onClick={close}
        className={cn(
          "fixed inset-0 z-[55] duration-250 animate-in fade-in-0",
          kind === "popover" ? "max-md:bg-black/20" : "bg-black/20",
          kind === "signing" && "pointer-events-none",
        )}
      />
      <div
        key={kind === "signing" ? queue[0]?.id : kind}
        role="dialog"
        aria-label={t(kind === "signing" ? "wallet.request" : "wallet.card.title")}
        data-wallet={kind}
        // pointer-events-auto: a modal dialog (New conversation, Buy) turns the page's pointer events off; the wallet stays usable above it
        className={cn(
          "pointer-events-auto fixed z-[60] flex flex-col overflow-hidden border bg-popover text-popover-foreground shadow-2xl duration-250 md:origin-bottom-left animate-in fade-in-0 max-md:slide-in-from-bottom-8 md:zoom-in-95",
          FRAME[kind],
        )}
      >
        {kind === "signing" ? <SigningPanel /> : kind === "popover" ? <WalletPopover /> : <WalletPanel />}
      </div>
    </>
  );
}
